/**
 * 💳 useAffiliatePayouts Hook
 * Provides payout data and actions
 */

import { useState, useEffect, useCallback } from 'react';
import {
  MOCK_PAYOUTS,
  MOCK_PAYOUT_SUMMARY,
  getPayoutStatusConfig,
  type Payout,
} from '@/lib/affiliate-mock-data';

// ============================================
// TYPES
// ============================================

interface PayoutSummary {
  pendingPayout: number;
  nextPayoutDate: string;
  lifetimePaid: number;
  paymentMethod: string;
  iban: string;
}

interface UsePayoutsReturn {
  payouts: Payout[];
  summary: PayoutSummary;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  downloadInvoice: (payoutId: number) => Promise<boolean>;
  downloadAllInvoices: () => Promise<boolean>;
  getStatusBadge: typeof getPayoutStatusConfig;
}

// ============================================
// HOOK IMPLEMENTATION
// ============================================

export function useAffiliatePayouts(): UsePayoutsReturn {
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [summary, setSummary] = useState<PayoutSummary>(MOCK_PAYOUT_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 200));
      setPayouts(MOCK_PAYOUTS);
      setSummary(MOCK_PAYOUT_SUMMARY);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch payouts'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const downloadInvoice = useCallback(async (payoutId: number): Promise<boolean> => {
    const payout = payouts.find(p => p.id === payoutId);
    if (!payout?.invoiceAvailable) return false;
    
    // In production, this would trigger a file download
    console.log(`Downloading invoice for payout ${payout.reference}`);
    return true;
  }, [payouts]);

  const downloadAllInvoices = useCallback(async (): Promise<boolean> => {
    const availableInvoices = payouts.filter(p => p.invoiceAvailable);
    console.log(`Downloading ${availableInvoices.length} invoices`);
    return true;
  }, [payouts]);

  return {
    payouts,
    summary,
    isLoading,
    error,
    refetch: fetchData,
    downloadInvoice,
    downloadAllInvoices,
    getStatusBadge: getPayoutStatusConfig,
  };
}

export default useAffiliatePayouts;
